import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  Mutation,
  MutationSetVisitorByIdArgs,
  Query,
  QueryGetVisitorByIdArgs,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import { SET_VISITOR_BY_ID_WITH_ROLE } from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_DETAILS,
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import useUserFormVisitorGroups from 'v2/hooks/useUserFormVisitorGroups/useUserFormVisitorGroups';
import { preserveExistingMembershipDetails, updateVisitorGroupMembershipWithAdminGroup } from './utils';

interface Props extends BaseHookProps {
  visitorId: string;
}

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_DETAILS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorById'>,
  QueryGetVisitorByIdArgs
> = GET_VISITOR_DETAILS;
const SET_VISITOR_BY_ID_WITH_ROLE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'setVisitorById'>,
  MutationSetVisitorByIdArgs
> = SET_VISITOR_BY_ID_WITH_ROLE;

const useEditVisitorGroups = ({ visitorId, handleFetchError }: Props) => {
  const { translation } = useTranslation();
  const { coContractors, administratorsVisitorGroups, installersVisitorGroups, visitorGroupsLoading } =
    useUserFormVisitorGroups({ handleFetchError, translation });

  const {
    loading: visitorDetailsLoading,
    error: visitorDetailsError,
    data: visitorDetails
  } = useQuery(GET_VISITOR_DETAILS_TYPED, {
    variables: { id: visitorId! }
  });

  useEffect(() => {
    if (visitorDetailsError) {
      handleFetchError(translation.user_details_error_fetching);
      console.error(visitorDetailsError);
    }
  }, [visitorDetailsError]);

  const [fetchVisitorDetails, { loading: fetchVisitorDetailsLoading, error: fetchVisitorDetailsError }] =
    useLazyQuery(GET_VISITOR_DETAILS_TYPED);

  useEffect(() => {
    if (fetchVisitorDetailsError) {
      handleFetchError(translation.user_details_error_fetching);
      console.error(fetchVisitorDetailsError);
    }
  }, [fetchVisitorDetailsError]);

  const [updateVisitorMutation, { loading: updateVisitorLoading }] = useMutation(SET_VISITOR_BY_ID_WITH_ROLE_TYPED, {
    refetchQueries: [
      { query: GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS },
      { query: GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS },
      { query: GET_VISITOR_DETAILS, variables: { id: visitorId } },
      // Keeps the members of the groups fresh for the user's Access overview tab.
      { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }
    ],
    awaitRefetchQueries: true,
    onError: (error) => {
      if (error.message === 'Visitor already exists') handleFetchError(translation.user_email_already_exists);
      else if (error.message === 'Visitor is not only CO_CONTRACTOR')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_adding);
    }
  });

  const updateVisitorGroupsMembership = async (
    visitorGroupMembership: VisitorGroupMembershipInput[],
    shouldBeAdmin: boolean,
    shouldBeInstaller: boolean
  ) => {
    // The save sends the user's full membership list. The ATLAS group memberships come from the form as group ids, but
    // the Administrator and Installer roles come as on/off flags: each role is a membership of the Administrators or
    // Installers group, so those two groups' ids are needed to add or remove it. A group's id never changes, so the ids
    // are read from the groups already loaded instead of fetching the groups again. The member ids that do change are
    // refreshed by the mutation's refetch of the slim groups query.
    if (administratorsVisitorGroups === undefined || installersVisitorGroups === undefined) {
      handleFetchError(translation.user_groups_error_updating);
      return undefined;
    }

    let visitorDetailsData;
    try {
      visitorDetailsData = await fetchVisitorDetails({ variables: { id: visitorId! } });
    } catch {
      handleFetchError(translation.user_groups_error_updating);
      return undefined;
    }
    const visitor = visitorDetailsData.data?.getVisitorById;
    if (!visitor) {
      handleFetchError(translation.user_groups_error_updating);
      return undefined;
    }

    const { name, id, visitorGroupMembership: visitorDetailsGroupMembership } = visitor;

    const entraMembership = visitorDetailsGroupMembership.find(
      (group) => group.visitorGroup.type === VisitorGroupType.AzureAd
    );
    const isEntraAlreadyIncluded = visitorGroupMembership.some(
      (membership) => membership.visitorGroupId === entraMembership?.visitorGroup.id
    );
    const membershipWithEntra =
      entraMembership && !isEntraAlreadyIncluded
        ? [...visitorGroupMembership, { visitorGroupId: entraMembership.visitorGroup.id, role: entraMembership.role }]
        : visitorGroupMembership;

    const updatedVisitorGroupMembership: VisitorGroupMembershipInput[] = updateVisitorGroupMembershipWithAdminGroup({
      visitorGroupMembership: membershipWithEntra,
      options: {
        shouldBeAdmin,
        adminGroup: administratorsVisitorGroups[0],
        shouldBeInstaller,
        installerGroup: installersVisitorGroups[0]
      }
    });

    return updateVisitorMutation({
      variables: {
        name,
        visitorId: id,
        visitorGroupMembership: preserveExistingMembershipDetails(
          updatedVisitorGroupMembership,
          visitorDetailsGroupMembership
        )
      }
    });
  };

  return {
    coContractors,
    visitorGroupsLoading,
    visitorDetails: visitorDetails?.getVisitorById,
    visitorDetailsLoading,
    administratorsVisitorGroups,
    installersVisitorGroups,
    updateVisitorGroupsMembership,
    updateVisitorGroupsMembershipLoading:
      updateVisitorLoading || visitorDetailsLoading || fetchVisitorDetailsLoading || visitorGroupsLoading
  };
};

export default useEditVisitorGroups;
